import type { AudioNodeProps } from '@/components/nodes/audio';
import type { CodeNodeProps } from '@/components/nodes/code';
import type { FileNodeProps } from '@/components/nodes/file';
import type { ImageNodeProps } from '@/components/nodes/image';
import type { VideoNodeProps } from '@/components/nodes/video';
import type { TextNodeProps } from '@/components/nodes/text';
import type { TweetNodeProps } from '@/components/nodes/tweet';
import type { UploadNodeProps } from '@/components/nodes/upload';
import type { Node } from '@xyflow/react';

/**
 * 前端是否保留 Asset:// 直传。
 * 生成链路统一传 HTTP URL，由各渠道按 URL 查素材库（百度/腾讯/sd），故默认不保留。
 */
const ASSET_REF_SUPPORTED_MODEL_IDS = new Set<string>([
  // 需要某模型前端直传 Asset:// 时再加入
]);

export function isAssetRefSupportedModel(modelId: string): boolean {
  return ASSET_REF_SUPPORTED_MODEL_IDS.has(modelId);
}

/**
 * 把一个 upload 节点（fileType=image）展开为下游消费者能直接使用的图片数组。
 *
 * - 普通图片：1 张（取 content）
 * - 主体库 multi 模式（subjectImages 长度 > 1）：N 张原图，按数组顺序排
 *
 * 注意：Asset:// 解析由调用方按模型决定（保持与现有逻辑一致）。
 */
export function getUploadNodeImages(
  data: UploadNodeProps['data'] | undefined,
): { url: string; type: string; name?: string }[] {
  if (!data || data.fileType !== 'image') return [];
  const subj = Array.isArray((data as any)?.subjectImages)
    ? ((data as any).subjectImages as unknown[])
        .map((u) => (typeof u === 'string' ? u.trim() : ''))
        .filter((u) => u.length > 0)
    : [];
  if (subj.length > 1) {
    const mime = data.content?.type || 'image/png';
    return subj.map((url) => ({ url, type: mime }));
  }
  if (data.content?.url) return [data.content];
  return [];
}

/**
 * 将 Asset:// URL 替换为节点数据中的 assetPreviewUrl。
 * 用于不支持 Asset:// 协议的模型。
 */
export function resolveNodeAssetRefUrls<T extends { url: string }>(
  items: T[],
  nodes: Node[],
): T[] {
  return items.map((item) => {
    if (!item.url || !item.url.startsWith('Asset://')) return item;
    for (const node of nodes) {
      const d = node.data as any;
      const urls = [d?.generated?.url, d?.generated?.urls?.[0], d?.content?.url].filter(Boolean);
      if (urls.includes(item.url) && d?.assetPreviewUrl) {
        return { ...item, url: d.assetPreviewUrl };
      }
    }
    return item;
  });
}

function resolveImageNodeOutput(data: ImageNodeProps['data'] | undefined) {
  if (!data) return undefined;
  const generated = (data as any)?.generated;
  const content = (data as any)?.content;
  const viewMode = data?.viewMode;

  if (viewMode === 'primitive') return content;
  if (viewMode === 'transform') {
    const url = generated?.url || generated?.sizeUrl || generated?.urls?.[0];
    return url ? { ...generated, url } : undefined;
  }

  const generatedUrl = generated?.url || generated?.sizeUrl || generated?.urls?.[0];
  if (generatedUrl) return { ...generated, url: generatedUrl };
  return content?.url ? content : undefined;
}

export const getTextFromTextNodes = (nodes: Node[]) => {
  const texts = nodes
    .filter((node) => node.type === 'text')
    .flatMap((node) => {
      const data = node.data as TextNodeProps['data'];
      const template = (data as any)?.promptTemplate?.content as string | undefined;

      const combine = (text?: string) => {
        if (!text) return null;
        return template ? `${template}\n\n${text}` : text;
      };

      // Transform 节点取 generated.text，Primitive 节点取 text
      const viewMode = data?.viewMode;
      let textValue: string | undefined;
      if (viewMode === 'transform') {
        textValue = data?.generated?.text;
      } else if (viewMode === 'primitive') {
        textValue = data?.text;
      } else {
        // 向后兼容：如果没有 viewMode，则根据是否有 generated.text 推断
        // 有 generated.text 则认为是 transform，否则是 primitive
        textValue = data?.generated?.text || data?.text;
      }

      const results = [combine(textValue)].filter(Boolean) as string[];
      return results;
    });

  return texts;
};

export const getTranscriptionFromAudioNodes = (nodes: Node[]) => {
  const transcripts = nodes
    .filter((node) => node.type === 'audio')
    .map((node) => (node.data as AudioNodeProps['data']).transcript)
    .filter(Boolean) as string[];

  return transcripts;
};

export const getDescriptionsFromImageNodes = (nodes: Node[]) => {
  const descriptions = nodes
    .filter((node) => node.type === 'image')
    .map((node) => (node.data as ImageNodeProps['data']).description)
    .filter(Boolean) as string[];

  return descriptions;
};

export const getImagesFromImageNodes = (nodes: Node[]) => {
  // 按节点ID排序以确保顺序稳定，与序号计算保持一致
  const sortedNodes = [...nodes]
    .filter((node) => node.type === 'image')
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  
  const images = sortedNodes
    .map((node) => {
      const data = node.data as ImageNodeProps['data'];
      return resolveImageNodeOutput(data);
    })
    .filter(Boolean) as { url: string; type: string }[];

  return images;
};

export const getImagesFromUploadNodes = (nodes: Node[]) => {
  // 按节点ID排序以确保顺序稳定，与序号计算保持一致
  const sortedNodes = [...nodes]
    .filter((node) => {
      if (node.type !== 'upload') return false;
      const data = node.data as UploadNodeProps['data'];
      // 只返回 fileType 为 'image' 的上传节点
      return data?.fileType === 'image' && data?.content;
    })
    .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  
  const images = sortedNodes
    .map((node) => {
      const data = node.data as UploadNodeProps['data'];
      return data.content;
    })
    .filter(Boolean) as { url: string; type: string; name?: string }[];

  return images;
};

/**
 * 获取所有图像（包括图像节点和上传节点），按连接顺序排序
 * 这个函数需要配合边的信息来保持与序号计算一致
 * @param nodes 节点数组（通常来自 getIncomers）
 * @param edges 边数组（用于确定连接顺序）
 * @param targetId 目标节点ID（可选，如果提供则按连接到该目标的边的顺序排序）
 */
export const getAllImagesFromNodes = (nodes: Node[], edges?: any[], targetId?: string) => {
  // 如果提供了 targetId 和 edges，按边的顺序排序
  if (targetId && edges && Array.isArray(edges)) {
    // 获取连接到目标的所有图像输入边，保持边的原始顺序
    const incomingEdges = edges.filter((e) => String(e?.target) === String(targetId));
    const imageSourceEdges = incomingEdges.filter((e) => {
      const src = nodes.find((n) => n.id === e?.source);
      if (!src) return false;
      if (src.type === 'image') return true;
      if (src.type === 'tool') {
        const url = (src as any)?.data?.tool?.params?.source_image_url;
        return typeof url === 'string' && url.trim().length > 0;
      }
      if (src.type === 'upload') {
        const fileType = (src.data as any)?.fileType;
        const contentType = (src.data as any)?.content?.type;
        return fileType === 'image' || (contentType && String(contentType).startsWith('image/'));
      }
      return false;
    });
    
    // 按边的顺序收集图像数据
    const images: Array<{ url: string; type: string; name?: string }> = [];
    // Always prioritize tool-node image first (then follow edge order for the rest)
    const toolEdges = imageSourceEdges.filter((e) => {
      const node = nodes.find((n) => n.id === e?.source);
      return Boolean(node && node.type === 'tool');
    });
    const otherEdges = imageSourceEdges.filter((e) => !toolEdges.includes(e));

    for (const edge of [...toolEdges, ...otherEdges]) {
      const node = nodes.find((n) => n.id === edge?.source);
      if (!node) continue;
      
      if (node.type === 'image') {
        const data = node.data as ImageNodeProps['data'];
        const imageData = resolveImageNodeOutput(data);
        if (imageData?.url) {
          images.push(imageData);
        }
      } else if (node.type === 'tool') {
        const url = (node as any)?.data?.tool?.params?.source_image_url;
        if (typeof url === 'string' && url.trim()) {
          images.push({ url: url.trim(), type: 'image/png' });
        }
      } else if (node.type === 'upload') {
        const data = node.data as UploadNodeProps['data'];
        if (data?.fileType === 'image') {
          // multi 主体节点会展开成多张原图（按 subjectImages 数组顺序），普通节点仍是 1 张
          for (const img of getUploadNodeImages(data)) {
            images.push(img);
          }
        }
      }
    }
    
    return images;
  }
  
  // 否则保持 nodes 数组的原始顺序（向后兼容）
  const imageSources: Array<{ id: string; type: 'image' | 'upload'; data: any }> = [];
  
  for (const node of nodes) {
    if (node.type === 'image') {
      const data = node.data as ImageNodeProps['data'];
      const imageData = resolveImageNodeOutput(data);
      const url = imageData?.url;
      if (url) {
        imageSources.push({
          id: node.id,
          type: 'image',
          data: imageData,
        });
      }
    } else if (node.type === 'tool') {
      const url = (node as any)?.data?.tool?.params?.source_image_url;
      if (typeof url === 'string' && url.trim()) {
        imageSources.push({
          id: node.id,
          type: 'image',
          data: { url: url.trim(), type: 'image/png' },
        });
      }
    } else if (node.type === 'upload') {
      const data = node.data as UploadNodeProps['data'];
      if (data?.fileType === 'image') {
        // multi 主体节点会展开成多张原图（按 subjectImages 数组顺序），普通节点仍是 1 张
        for (const img of getUploadNodeImages(data)) {
          imageSources.push({
            id: node.id,
            type: 'upload',
            data: img,
          });
        }
      }
    }
  }
  
  return imageSources.map((item) => item.data).filter(Boolean) as Array<{ url: string; type: string; name?: string }>;
};

export const getVideosFromVideoNodes = (nodes: Node[]) => {
  const videos = nodes
    .filter((node) => node.type === 'video')
    .map((node) => {
      const d = node.data as VideoNodeProps['data'];
      // Transform 节点取 generated，Primitive 节点取 content
      const viewMode = d?.viewMode;
      let video;
      if (viewMode === 'transform') {
        video = d?.generated;
      } else if (viewMode === 'primitive') {
        video = d?.content;
      } else {
        // 向后兼容：如果没有 viewMode，则根据是否有 generated 推断
        // 有 generated 则认为是 transform，否则是 primitive
        video = d?.generated || d?.content;
      }
      if (!video) return null;
      return { ...video, processingTaskId: (d as any)?.processingTaskId } as { url: string; type: string; processingTaskId?: string };
    })
    .filter(Boolean) as { url: string; type: string; processingTaskId?: string }[];

  return videos;
};

export const getVideosFromUploadNodes = (nodes: Node[]) => {
  const videos = nodes
    .filter((node) => node.type === 'upload')
    .map((node) => {
      const data = node.data as UploadNodeProps['data'];
      // 只返回 fileType 为 'video' 的上传节点
      if (data?.fileType === 'video' && data?.content) {
        return data.content;
      }
      return null;
    })
    .filter(Boolean) as { url: string; type: string; name?: string }[];

  return videos;
};

export const getAudiosFromAudioNodes = (nodes: Node[]) => {
  const audios = nodes
    .filter((node) => node.type === 'audio')
    .map((node) => {
      const data = node.data as AudioNodeProps['data'];
      // Transform 节点取 generated，Primitive 节点取 content
      const viewMode = data?.viewMode;
      let audio;
      if (viewMode === 'transform') {
        audio = data?.generated;
      } else if (viewMode === 'primitive') {
        audio = data?.content;
      } else {
        // 向后兼容：如果没有 viewMode，则根据是否有 generated 推断
        // 有 generated 则认为是 transform，否则是 primitive
        audio = data?.generated || data?.content;
      }
      return audio;
    })
    .filter(Boolean) as { url: string; type: string }[];

  return audios;
};

export const getAudiosFromUploadNodes = (nodes: Node[]) => {
  const audios = nodes
    .filter((node) => node.type === 'upload')
    .map((node) => {
      const data = node.data as UploadNodeProps['data'];
      // 只返回 fileType 为 'audio' 的上传节点
      if (data?.fileType === 'audio' && data?.content) {
        return data.content;
      }
      return null;
    })
    .filter(Boolean) as { url: string; type: string; name?: string }[];

  return audios;
};

/**
 * 收集来自上游 Upload/Image 节点的提示词：
 * - Upload 节点使用 data.prompt
 * - Image 节点若有 data.instructions 也纳入
 */
export const getPromptFromUploadNodes = (nodes: Node[]) => {
  try {
    const prompts = (Array.isArray(nodes) ? nodes : [])
      // 仅从 upload 节点收集提示词，避免把上游 image 节点的 instructions 拼入
      .filter((n: any) => n && n.type === 'upload')
      .map((n: any) => {
        const d = (n as any)?.data || {};
        if (typeof d.prompt === 'string' && d.prompt.trim().length > 0) {
          return d.prompt.trim();
        }
        // 兜底：极端情况下 upload 节点也可能带有 instructions
        if (typeof d.instructions === 'string' && d.instructions.trim().length > 0) {
          return d.instructions.trim();
        }
        return '';
      })
      .filter((s: string) => s.length > 0);
    return prompts as string[];
  } catch {
    return [] as string[];
  }
};

/**
 * 收集来自上游 Tool 节点的提示词：
 * - Tool 节点使用 data.tool.params.prompt（例如 camera 工具生成的 prompt）
 */
export const getPromptFromToolNodes = (nodes: Node[]) => {
  try {
    const prompts = (Array.isArray(nodes) ? nodes : [])
      .filter((n: any) => n && n.type === 'tool')
      .map((n: any) => {
        const p = (n as any)?.data?.tool?.params?.prompt;
        return typeof p === 'string' ? p.trim() : '';
      })
      .filter((s: string) => s.length > 0);
    // de-dupe while preserving order
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of prompts) {
      if (seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  } catch {
    return [] as string[];
  }
};

// 主体多视角系统提示（模板）
export const SUBJECT_MULTI_VIEW_HINT_TEMPLATE =
  '[system: The provided reference image(s) ({{prompt}}) all depict the same subject from different angles, poses or details. Treat them as views of one single unified subject when generating. Do NOT duplicate the subject, do NOT arrange the views as a grid or collage in the output, and do NOT include any extra subjects beyond what is described.]';
function formatSubjectMultiViewHint(prompts: string[]): string {
  const inner = (Array.isArray(prompts) ? prompts : [])
    .map((s) => String(s || '').trim())
    .filter((s) => s.length > 0)
    .join(' ; ');
  return SUBJECT_MULTI_VIEW_HINT_TEMPLATE.replace('{{prompt}}', inner);
}

/**
 * 检查是否存在来自主体库的上传节点（data.subject === true），如果有则返回内置系统提示
 */
export const getSubjectSystemHintsFromUploadNodes = (nodes: Node[]) => {
  try {
    const list = (Array.isArray(nodes) ? nodes : [])
      .filter((n: any) => n && n.type === 'upload' && Boolean((n as any)?.data?.subject))
      .map((n: any) => {
        const d = (n as any)?.data || {};
        const p = typeof d.prompt === 'string' ? d.prompt.trim() : '';
        return p;
      })
      .filter((s: string) => s.length > 0);
    if (list.length === 0) return [];
    return [formatSubjectMultiViewHint(list)];
  } catch {
    return [] as string[];
  }
};
export const isValidSourceTarget = (source: Node, target: Node) => {
  // Disallow edges from the generic drop node entirely
  if (source.type === 'drop') {
    return false;
  }

  // 允许视频连接到视频或图片（图片保持 Primitive 视图，仅作为“工作流占位”）
  if (source.type === 'video') {
    return target.type === 'video' || target.type === 'image';
  }

  // Audio target can only accept text, audio, or audio-type upload as a source
  if (target.type === 'audio') {
    const isAudioUpload =
      source.type === 'upload' &&
      (source.data as UploadNodeProps['data'])?.fileType === 'audio';
    if (source.type !== 'text' && source.type !== 'audio' && !isAudioUpload) {
      return false;
    }
  }

  // Disallow edges into file nodes
  if (target.type === 'file') {
    return false;
  }

  return true;
};

export const getCodeFromCodeNodes = (nodes: Node[]) => {
  const sourceCodes = nodes
    .filter((node) => node.type === 'code')
    .map((node) => (node.data as CodeNodeProps['data']).content)
    .filter(Boolean) as { text: string; language: string }[];

  const generatedCodes = nodes
    .filter((node) => node.type === 'code' && node.data.generated)
    .map((node) => (node.data as CodeNodeProps['data']).generated)
    .filter(Boolean) as { text: string; language: string }[];

  return [...sourceCodes, ...generatedCodes];
};

export const getFilesFromFileNodes = (nodes: Node[]) => {
  const files = nodes
    .filter((node) => node.type === 'file')
    .map((node) => (node.data as FileNodeProps['data']).content)
    .filter(Boolean) as { url: string; type: string; name: string }[];

  return files;
};

export const getTextFromFileNodes = (nodes: Node[]) => {
  const texts = nodes
    .filter((node) => node.type === 'file')
    .map((node) => (node.data as FileNodeProps['data']).text)
    .filter(Boolean) as string[];

  return texts;
};

export const getTweetContentFromTweetNodes = (nodes: Node[]) => {
  const tweets = nodes
    .filter((node) => node.type === 'tweet')
    .map((node) => (node.data as TweetNodeProps['data']).content)
    .filter(Boolean) as NonNullable<TweetNodeProps['data']['content']>[];

  const tweetContent = tweets.map(
    (tweet) => `On ${tweet.date}, ${tweet.author} tweeted: ${tweet.text}`
  );

  return tweetContent;
};

// ===== Frame role helpers for image → video edges =====

export type FrameRole = 'start' | 'end' | 'reference' | undefined;

/**
 * 计算图像节点（包括图像上传节点和图像节点）连接到目标图像节点时的序号
 * @param sourceId 源节点ID（图像上传节点或图像节点）
 * @param getNodes 获取所有节点的函数
 * @param getEdges 获取所有边的函数
 * @returns 序号（从1开始），如果没有连接到图像节点则返回 null
 */
// (Removed) getImageUploadNodeIndex: node auto-indexing has been removed
